import { MonthDayProps } from '@oberoninternal/travelbase-ds/components/calendar/PlannerMonth';
import Special from '@oberoninternal/travelbase-ds/svg/Special';
import Package from '@oberoninternal/travelbase-ds/svg/Package';
import validInterval from '@oberoninternal/travelbase-ds/utils/validInterval';
import addDays from 'date-fns/addDays';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfToday from 'date-fns/startOfToday';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import parseToDateString from '../../constants/parseToDateString';
import { PlannerTripFragment, TripTypeEnum } from '../../generated/graphql';
import { getDateStringsFromTrip } from '../../utils/trip';
import { PlannerState } from './usePlannerPeriod';
import isAfter from 'date-fns/isAfter';

const containsDiscount = (trip: PlannerTripFragment) =>
    !!(trip.type !== TripTypeEnum.Regular && trip.originalPrice && trip.price < trip.originalPrice);
const containsUpsell = (trip: PlannerTripFragment) =>
    trip.type !== TripTypeEnum.Regular &&
    (!trip.originalPrice || (trip.originalPrice && trip.price > trip.originalPrice));
const getDayIcons = (discountAvailable: boolean, upsellAvailable: boolean) => (
    <>
        {discountAvailable && <Special />}
        {upsellAvailable && <Package />}
    </>
);
// A one night stay is the price of that night, without one (e.g. a minimum stay) the shortest stay is averaged instead
const getNightTrip = (dayTrips: PlannerTripFragment[]) =>
    [...dayTrips].sort((a, b) => a.duration - b.duration || a.price - b.price)[0];

const usePlannerModifier = (
    state: PlannerState,
    trips: Map<string, PlannerTripFragment[]>,
    showPricePerNight = false
) => {
    const intl = useIntl();
    const longestTrips = useMemo(
        () => [...trips.entries()].map(([, dayTrips]) => [...dayTrips].sort((a, b) => b.duration - a.duration)[0]),
        [trips]
    );
    const { period, selecting } = state;
    const getDayTrips = useCallback((date: Date | number) => trips.get(parseToDateString(date)) ?? [], [trips]);
    // These trips are sorted by price
    const potentialDayTrips = useMemo(
        () => (period?.start ? [...getDayTrips(period.start)].sort((a, b) => a.price - b.price) : undefined),
        [getDayTrips, period]
    );

    const formatPrice = useCallback(
        ({ price, duration }: Pick<PlannerTripFragment, 'price' | 'duration'>) =>
            intl.formatNumber(showPricePerNight ? price / duration : price, { format: 'EUR_ROUNDED' }),
        [intl, showPricePerNight]
    );
    const computeDayProps = useCallback(
        (day: Date): MonthDayProps => {
            const { start, end } = period ?? {};

            if (isBefore(day, startOfToday())) {
                return { variant: 'disabled' };
            }
            const dayTrips = getDayTrips(day);

            if (start && isSameDay(day, start)) {
                const discountAvailable = dayTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                return {
                    variant: 'selected-start',
                    icon: getDayIcons(discountAvailable, upsellAvailable),
                };
            }

            if (start && end && isSameDay(day, end) && potentialDayTrips) {
                const sameDurationTrips = potentialDayTrips.filter(
                    ({ duration }) => differenceInCalendarDays(end, start) === duration
                );
                const discountAvailable = sameDurationTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                const [bestTrip] = sameDurationTrips;
                const label = bestTrip ? formatPrice(bestTrip) : undefined;

                return {
                    variant: 'selected-end',
                    label,
                    icon: getDayIcons(discountAvailable, upsellAvailable),
                };
            }

            if (selecting && start && potentialDayTrips) {
                for (const { duration, price } of potentialDayTrips) {
                    const label = formatPrice({ duration, price });
                    const discountAvailable = potentialDayTrips.some(
                        trip => trip.duration === duration && containsDiscount(trip)
                    );
                    const upsellAvailable = dayTrips.some(containsUpsell);

                    const potential = addDays(start, duration);
                    if (isSameDay(potential, day)) {
                        if (end && isWithinInterval(day, { start, end })) {
                            return {
                                variant: 'bookable-within',
                                label,
                                icon: getDayIcons(discountAvailable, upsellAvailable),
                            };
                        }
                        return {
                            variant: 'bookable-outside',
                            label,
                            icon: getDayIcons(discountAvailable, upsellAvailable),
                        };
                    }
                }
            }

            if (validInterval(period) && isAfter(period.end, period.start) && isWithinInterval(day, period)) {
                const discountAvailable = dayTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                return {
                    variant: !selecting && dayTrips.length > 0 ? 'bookable-within' : 'within',
                    icon: getDayIcons(discountAvailable, upsellAvailable),
                };
            }

            if (!selecting) {
                const discountAvailable = dayTrips.some(containsDiscount);
                const upsellAvailable = dayTrips.some(containsUpsell);

                if (dayTrips.length > 0) {
                    return {
                        variant: 'bookable',
                        label: showPricePerNight ? formatPrice(getNightTrip(dayTrips)) : undefined,
                        icon: getDayIcons(discountAvailable, upsellAvailable),
                    };
                }
            }

            if (
                longestTrips.some(trip => {
                    const { arrivalDate, departureDate } = getDateStringsFromTrip(trip);
                    return isWithinInterval(day, { start: new Date(arrivalDate), end: new Date(departureDate) });
                })
            ) {
                return { variant: 'default' };
            }

            return { variant: 'blocked' };
        },
        [formatPrice, getDayTrips, period, potentialDayTrips, selecting, longestTrips, showPricePerNight]
    );

    return computeDayProps;
};

export default usePlannerModifier;
